<template>
  <v-card>
    <v-card-title class="headline d-flex justify-space-between align-center py-2">
      Gestionar membresía
      <v-btn icon :disabled="saving" @click="close"><v-icon>mdi-close</v-icon></v-btn>
    </v-card-title>
    <v-divider />

    <div v-if="loadingMembership" class="d-flex justify-center py-8">
      <v-progress-circular indeterminate color="primary" size="48" />
    </div>

    <template v-else-if="membershipToEdit">
      <!-- Resumen (mismo formato que el modal de renovación: contenedor gris) -->
      <v-card-text class="pt-4 pb-0">
        <div class="d-flex align-baseline justify-space-between mb-2">
          <div class="primary--text" style="font-weight: bold; font-size: 1.2rem">
            {{ membershipToEdit.pet ? membershipToEdit.pet.name : '' }}
          </div>
          <div class="text-caption grey--text text--darken-1">
            Ref. {{ membershipToEdit.ref }}
          </div>
        </div>
        <v-sheet class="grey lighten-4 rounded pa-3">
          <v-row dense>
            <v-col cols="7" class="py-1">
              <div class="text-caption grey--text">Plan actual</div>
              <div class="font-weight-medium">
                {{ membershipToEdit.plan ? membershipToEdit.plan.name : '—' }}
                <span class="grey--text text--darken-1">({{ intervalLabel }})</span>
              </div>
            </v-col>
            <v-col cols="5" class="py-1">
              <div class="text-caption grey--text">Cliente</div>
              <div class="font-weight-medium">{{ clientName }}</div>
            </v-col>
            <template v-if="membershipToEdit.companyName">
              <v-col cols="7" class="py-1">
                <div class="text-caption grey--text">Empresa</div>
                <div class="font-weight-medium">{{ membershipToEdit.companyName }}</div>
              </v-col>
              <v-col cols="5" class="py-1">
                <div class="text-caption grey--text">Contrato</div>
                <div v-if="membershipToEdit.contratoCodigo" class="font-weight-medium">
                  {{ membershipToEdit.contratoCodigo }}
                </div>
                <div v-else class="grey--text">Sin contrato</div>
              </v-col>
            </template>
            <v-col v-else cols="12" class="py-1">
              <div class="text-caption grey--text">Empresa</div>
              <div class="grey--text">Sin empresa (B2C)</div>
            </v-col>
            <v-col cols="12" class="py-1">
              <div class="text-caption grey--text">Fecha de inicio</div>
              <div class="font-weight-medium">{{ startDateText }}</div>
            </v-col>
          </v-row>
        </v-sheet>
      </v-card-text>

      <!-- Datos de la membresía -->
      <membership-maintainer
        ref="membershipMaintainer"
        :membership="membershipToEdit"
        :embedded="true"
        @close-dialog="close"
      />

      <template v-if="canChangeStatus">
      <v-divider />
      <div class="px-6 pt-4">
        <div class="text-subtitle-1 font-weight-bold mb-1">Estado de la membresía</div>
      </div>
      <v-card-text class="py-2">
        <v-row justify="center" align-center>
          <v-radio-group v-model="selectedStatus" row class="text-center">
            <v-radio
              v-for="option in statusOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
              :color="getRadioColor(option.value)"
              class="mx-4"
            />
          </v-radio-group>
          <div class="mt-2 text-center" style="color: black" v-if="selectedStatusDescription">
            <strong>{{ selectedStatusDescription }}</strong>
          </div>
          <v-col cols="12" v-if="selectedStatus === 4" class="mt-3 px-0">
            <v-textarea
              v-model="deactivationReason"
              label="Motivo de desactivación *"
              outlined
              rows="3"
              :counter="300"
              maxlength="300"
              placeholder="Escribe el motivo de desactivación..."
              :rules="[v => !!v && v.trim().length > 0 || 'El motivo es obligatorio para desactivar']"
            />
          </v-col>
          <v-col cols="12" v-if="showSendEmailOption" class="py-0 px-0 d-flex justify-center">
            <v-checkbox
              v-model="sendEmail"
              :label="sendEmailLabel"
              hint="Desmárcalo para cambiar el estado sin avisar al cliente"
              persistent-hint
              class="mt-0"
            />
          </v-col>
        </v-row>
      </v-card-text>
      </template>
      <v-expansion-panels flat>
        <v-expansion-panel elevation="0">
          <v-expansion-panel-header>
            <span class="text-subtitle-2" style="font-weight: 600;">Historial de estados</span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card class="elevation-0">
              <v-card-text>
                <div v-if="statusHistoryLoading" class="d-flex justify-center py-4">
                  <v-progress-circular indeterminate color="primary" size="36" />
                </div>
                <div v-else-if="membershipStatusHistory.length === 0" class="text-center grey--text caption py-2">
                  Sin historial de cambios de estado.
                </div>
                <template v-else>
                  <p class="font-weight-bold text-center mb-0">Más reciente</p>
                  <v-timeline>
                    <v-timeline-item
                      v-for="item in sortedStatusHistory"
                      :key="item.id"
                      :color="getRadioColor(item.newStatus)"
                    >
                      <div class="d-flex flex-column align-center">
                        <div class="font-weight-medium">{{ getStatusLabel(item.newStatus) }}</div>
                        <span style="font-size: 12px;">{{ formatStatusDate(item.createdAt) }}</span>
                        <div v-if="item.deactivationReason" class="d-flex flex-column align-center">
                          <span style="font-size: 13px;"><u>Motivo:</u></span>
                          <p style="font-size: 13px; text-align: center;">"{{ item.deactivationReason }}"</p>
                        </div>
                      </div>
                    </v-timeline-item>
                  </v-timeline>
                  <p class="font-weight-bold text-center mt-0">Más antigua</p>
                </template>
              </v-card-text>
            </v-card>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <v-divider />
      <v-card-actions class="d-flex justify-end">
        <v-btn text color="grey darken-1" :disabled="saving" @click="close">Cerrar</v-btn>
        <v-btn v-if="canUpdateData || canChangeStatus" color="primary" :loading="saving" :disabled="!selectedStatus || (selectedStatus === 4 && !deactivationReason.trim())" @click="save" class="ma-2">
          Guardar cambios
        </v-btn>
      </v-card-actions>
    </template>
  </v-card>
</template>

<script>
import MembershipMaintainer from './MembershipMaintainer';
import MembershipsService from '../services/memberships.service';
import Api360Service from '../services/360.service.js';
import DateUtils from '../utils/date.utils.js';
import { canAct } from '../utils/permissions';

const STATUS_OPTIONS = [
  { label: 'Pendiente de pago', value: 5 },
  { label: 'En revisión', value: 6 },
  { label: 'Desactivado', value: 4 },
  { label: 'Activo', value: 2 },
];

const STATUS_DESCRIPTIONS = {
  2: 'Recuerda verificar que los pagos están al día antes de activar la membresía.',
  3: 'Recuerda que solo aplica en descuento por planilla.',
};

const EMAIL_STATUS_ACTIVE = 2;
const EMAIL_STATUS_DEACTIVATED = 4;

const RADIO_COLORS = {
  2: 'green',
  4: 'red',
  5: 'blue',
  6: 'orange',
};

/**
 * Gestor unificado de una membresía: edición de datos (vía MembershipMaintainer)
 * y cambio de estado con motivo e historial, en una sola vista. Se usa tanto en
 * Vision360 como en la vista de Membresías. Se monta dentro de un v-dialog.
 */
export default {
  name: 'MembershipManager',
  components: { MembershipMaintainer },
  props: {
    // Membresía a gestionar; requiere al menos `id` y, opcionalmente, `status`.
    membership: { type: Object, required: true },
    // Token para las llamadas de estado/historial (Api360Service).
    token: { type: String, default: null },
  },
  data() {
    return {
      loadingMembership: false,
      saving: false,
      membershipToEdit: null,
      selectedStatus: null,
      deactivationReason: '',
      sendEmail: true,
      membershipStatusHistory: [],
      statusHistoryLoading: false,
      statusOptions: STATUS_OPTIONS,
      statusDescriptions: STATUS_DESCRIPTIONS,
    };
  },
  computed: {
    // The single save button covers two actions, each with its own tag (spec 132).
    canUpdateData() {
      return canAct('memberships:update');
    },
    canChangeStatus() {
      return canAct('memberships:change-status');
    },
    // Token para Api360Service: usa el prop si llega; si no, cae a localStorage,
    // así el componente funciona en vistas que no inyectan el token.
    authToken() {
      if (this.token) return this.token;
      try {
        return JSON.parse(localStorage.getItem('accessToken'));
      } catch {
        return null;
      }
    },
    intervalLabel() {
      const interval = this.membershipToEdit && this.membershipToEdit.paymentInterval;
      if (interval === 1) return 'anual';
      if (interval === 2) return 'mensual';
      return '—';
    },
    clientName() {
      const user = this.membershipToEdit && this.membershipToEdit.user;
      if (!user) return '—';
      return `${user.name ?? ''} ${user.surname ?? ''}`.trim() || '—';
    },
    startDateText() {
      const raw = this.membershipToEdit && this.membershipToEdit.startDate;
      if (!raw) return '—';
      const [year, month, day] = String(raw).substr(0, 10).split('-');
      return day && month && year ? `${day}/${month}/${year}` : String(raw);
    },
    selectedStatusDescription() {
      return this.statusDescriptions[this.selectedStatus] || '';
    },
    // Solo activar (2) y desactivar (4) envían correo, y solo si el estado cambia.
    showSendEmailOption() {
      const originalStatus = this.membershipToEdit && this.membershipToEdit.status;
      return (
        [EMAIL_STATUS_ACTIVE, EMAIL_STATUS_DEACTIVATED].includes(this.selectedStatus) &&
        this.selectedStatus !== originalStatus
      );
    },
    sendEmailLabel() {
      return this.selectedStatus === EMAIL_STATUS_ACTIVE
        ? 'Enviar correo de activación al cliente'
        : 'Enviar correo de desactivación al cliente';
    },
    sortedStatusHistory() {
      return [...this.membershipStatusHistory].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
    },
  },
  watch: {
    // Cada cambio de estado vuelve al comportamiento por defecto (enviar correo).
    selectedStatus() {
      this.sendEmail = true;
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loadingMembership = true;
      try {
        const res = await MembershipsService.getMembershipById(this.membership.id);
        if (res.status === 'ok' && res.data) {
          this.membershipToEdit = res.data;
          this.selectedStatus = res.data.status ?? this.membership.status ?? null;
        } else {
          this.$emit('error', 'No se pudo cargar la membresía');
        }
      } catch (e) {
        this.$emit('error', 'Error al cargar la membresía');
      } finally {
        this.loadingMembership = false;
      }
      await this.loadStatusHistory();
    },
    async loadStatusHistory() {
      this.membershipStatusHistory = [];
      this.statusHistoryLoading = true;
      try {
        const history = await Api360Service.getMembershipStatusHistory(
          this.membership.id,
          this.authToken
        );
        this.membershipStatusHistory = Array.isArray(history)
          ? history
          : history?.data ?? [];
      } catch {
        this.membershipStatusHistory = [];
      } finally {
        this.statusHistoryLoading = false;
      }
    },
    // Botón único: guarda los datos y, si el estado cambió, aplica el cambio de
    // estado (con motivo e historial). Emite `saved` al terminar correctamente.
    async save() {
      if (!this.selectedStatus) return;
      if (this.selectedStatus === 4 && !this.deactivationReason.trim()) return;
      this.saving = true;
      try {
        const dataOk = !this.canUpdateData || (await this.$refs.membershipMaintainer.save());
        if (!dataOk) {
          this.$emit('error', 'No se pudieron guardar los datos de la membresía');
          return;
        }
        const originalStatus = this.membershipToEdit && this.membershipToEdit.status;
        if (this.canChangeStatus && this.selectedStatus !== originalStatus) {
          await Api360Service.updateMembershipStatus(
            this.membership.id,
            this.selectedStatus,
            this.authToken,
            this.deactivationReason?.trim() || null,
            this.sendEmail
          );
        }
        this.$emit('saved');
      } catch (e) {
        this.$emit('error', 'Error al guardar los cambios');
      } finally {
        this.saving = false;
      }
    },
    close() {
      this.$emit('close-dialog');
    },
    getStatusLabel(status) {
      const option = STATUS_OPTIONS.find((o) => o.value === status);
      return option ? option.label : `Estado ${status}`;
    },
    formatStatusDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      const date = DateUtils.formatToChileanDate(d);
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${date} - ${hh}:${mm}`;
    },
    getRadioColor(status) {
      return RADIO_COLORS[status] || 'primary';
    },
  },
};
</script>
